import { readPage, unwrapItem, type Page, type SessionClient } from '@portal/platform/http';
import type {
  Lead,
  LeadActivity,
  LeadActivityType,
  LeadsQuery,
  LeadTimeline,
  LeadUpdate,
} from './types';

/**
 * The leads endpoints, ported from `LeadsService`. The org scope rides on the session client
 * (`?orgNodeId=`); the backend refuses anything outside the caller's subtree.
 */

/** `GET /leads`'s `limit` maximum (`leadFilterSchema`). */
export const LEADS_MAX_LIMIT = 100;

const leadPath = (id: string) => `/leads/${encodeURIComponent(id)}`;

export async function fetchLeads(client: SessionClient, query: LeadsQuery): Promise<Page<Lead>> {
  const { body, headers } = await client.withHeaders('/leads', {
    query: {
      view: query.view,
      flow: query.flow,
      source: query.source,
      handled:
        query.handled !== undefined
          ? String(query.handled)
          : query.onlyUnhandled
            ? 'false'
            : undefined,
      callStatus: query.callStatus,
      search: query.search.trim() || undefined,
      sortBy: query.sort?.field,
      sortOrder: query.sort?.direction,
      page: query.page,
      limit: query.limit,
    },
  });
  return readPage<Lead>(body, headers);
}

/**
 * Every lead of the current filter, for the CSV export, {@link LEADS_MAX_LIMIT} at a time.
 * Angular asked for one page of 1000, which the endpoint refuses (400) — its export never worked.
 */
export async function fetchLeadsForExport(
  client: SessionClient,
  query: Omit<LeadsQuery, 'page' | 'limit'>,
): Promise<Lead[]> {
  const rows: Lead[] = [];
  for (let page = 1; ; page += 1) {
    const result = await fetchLeads(client, { ...query, page, limit: LEADS_MAX_LIMIT });
    rows.push(...result.items);
    const done =
      result.items.length < LEADS_MAX_LIMIT ||
      (result.total !== null ? rows.length >= result.total : !result.hasNext);
    if (done) {
      return rows;
    }
  }
}

export async function updateLead(
  client: SessionClient,
  id: string,
  patch: LeadUpdate,
): Promise<Lead> {
  return unwrapItem<Lead>(await client.json(leadPath(id), { method: 'PATCH', json: patch }));
}

/** `details` carries the call flow's outcome token and JSON record (see call-script/call-log.ts). */
export async function addLeadActivity(
  client: SessionClient,
  id: string,
  activityType: LeadActivityType,
  details: { outcome?: string; notes?: string } = {},
): Promise<LeadActivity> {
  return unwrapItem<LeadActivity>(
    await client.json(`${leadPath(id)}/activities`, {
      method: 'POST',
      json: { activityType, ...details },
    }),
  );
}

/**
 * Close a lead as lost. CONVERTED is deliberately unreachable from the UI: a lead that becomes an
 * order is closed by the backend, which links it to the order.
 */
export async function retireLeadAsLost(client: SessionClient, id: string): Promise<Lead> {
  return unwrapItem<Lead>(
    await client.json(`${leadPath(id)}/retire`, { method: 'PATCH', json: { outcome: 'LOST' } }),
  );
}

/**
 * Remove a lead that never belonged in the queue (a test order, a duplicate). Soft-deleted
 * server-side; gated on `lead:update` — there is no `lead:delete`.
 */
export async function deleteLead(client: SessionClient, id: string): Promise<void> {
  await client.json(leadPath(id), { method: 'DELETE' });
}

/** What the customer did in the customer portal, grouped into visits (`lead:read`). */
export async function fetchLeadTimeline(client: SessionClient, id: string): Promise<LeadTimeline> {
  return unwrapItem<LeadTimeline>(await client.json(`${leadPath(id)}/timeline`));
}

/** The token the backend groups session-less steps under. */
export const UNATTACHED_VISIT = 'unattached';

/** Global-admin cleanup (`platform:manage`): one step. There is deliberately no edit. */
export async function deleteTimelineStep(
  client: SessionClient,
  leadId: string,
  eventId: string,
): Promise<void> {
  await client.json(`${leadPath(leadId)}/timeline/events/${encodeURIComponent(eventId)}`, {
    method: 'DELETE',
  });
}

/** Global-admin cleanup: a whole visit (`sessionId` may be {@link UNATTACHED_VISIT}). */
export async function deleteTimelineVisit(
  client: SessionClient,
  leadId: string,
  sessionId: string,
): Promise<void> {
  await client.json(`${leadPath(leadId)}/timeline/visits/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
}
